'use client';

import { useTranslation } from 'next-i18next';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { Anchor } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import { useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';
import type { JobSearchQueryParams } from '@type/filters';
import { MemoizedCompactJobAlertCard } from './CompactJobAlertCard';
import { POPULAR_SEARCH_KEYS } from './popularSearchKeys';

interface GuestJobsSidebarProps {
  locale: Locale;
  className?: string;
  /** Opens the Jobs on Map modal. Provided by MissionControl to avoid import cycles. */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
}

/**
 * @description Sidebar for unauthenticated (guest) users.
 * Shows a sign-in prompt followed by popular job search cards.
 * Each card links directly to the public job list for that keyword.
 */
export const GuestJobsSidebar = ({
  locale, className, onOpenMap,
}: GuestJobsSidebarProps) => {
  const { t } = useTranslation('common');
  const { navigateToEditSearch } = useSidebarNavigation();

  return (
    <div className={`flex h-full flex-col bg-surface ${className ?? ''}`}>
      <div className="scrollbar-hide flex-1 overflow-y-auto p-4">
        {/* Popular Searches */}
        <div className="mb-3">
          <h3 className="mb-3 px-3 text-sm font-bold text-ink">
            {t('user.popular.searches')}
          </h3>
          <div className="flex flex-col gap-1">
            {POPULAR_SEARCH_KEYS.slice(0, 6).map((key) => {
              const keyword = t(key);
              return (
                <MemoizedCompactJobAlertCard
                  key={key}
                  title={keyword}
                  locale={locale}
                  href={getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale)}
                  onEdit={() => navigateToEditSearch({ keyword })}
                  editHref={`${getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale)}?edit=1`}
                  onOpenMap={onOpenMap}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom: sign-in row — mirrors SidebarAccountRow's position and height
          (avatar size-xs = 32px) for authenticated users. */}
      <Anchor
        href={`/login?_target_path=${encodeURIComponent(`/${locale}/mc`)}`}
        styling="none"
        className="m-2 flex shrink-0 items-center gap-3 rounded-2xl px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-150"
      >
        <UserCircleIcon className="size-8 shrink-0 text-ink" />
        <span>{t('menu.sign_in-register') || 'Sign In'}</span>
      </Anchor>
    </div>
  );
};
